import { isNil, isString, trim } from "lodash";

/**
 * Converts input into a `number`, or returns a fallback value when conversion is not valid.
 *
 * Invalid input includes:
 * - `undefined` or `null`
 * - strings that are empty/whitespace only
 * - values that result in `NaN` when passed to `Number(...)`
 *
 * Notes:
 * - If `fallbackValue` is omitted, `null` is returned for invalid input.
 * - Unlike `Number(...)`, this returns `null` (or the provided fallback) for `null` and empty-string input.
 *
 * @param value Value to convert.
 * @param fallbackValue Value returned when conversion is invalid. Defaults to `null`.
 * @returns Parsed numeric value, or fallback/null when invalid.
 */
export const convertToNumberOrFallback = <T extends number | null>(
  value: string | number | undefined | null,
  fallbackValue?: T,
) => {
  if (isNil(value) || (isString(value) && trim(value) === "") || isNaN(Number(value))) {
    return (fallbackValue ?? null) as T;
  }

  return Number(value);
};
